"use client"
import type React from "react"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import InputField from "../common/InputField"
import Button from "../common/Button"
import { Link } from "react-router-dom"
import { ArrowLeft, CheckCircle, Mail } from "lucide-react"

interface ForgotPasswordFormProps {
  onSubmit?: (email: string) => void
}

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({ onSubmit }) => {
  const [email, setEmail] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState(false)
  const navigate = useNavigate()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")
    setLoading(true)
    
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1500))
      setSuccess(true)
      // Redirect to reset password page after 2 seconds
      setTimeout(() => {
        navigate(`/reset-password?email=${encodeURIComponent(email)}`)
      }, 2000)
    } catch (err) {
      setError("Failed to send reset email. Please try again.")
    } finally {
      setLoading(false)
    }

    if (onSubmit) {
      onSubmit(email)
    }
  }

  if (success) {
    return (
      <div className="text-center space-y-4">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full">
          <CheckCircle className="text-green-600 w-8 h-8" />
        </div>
        <div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Reset link sent!</h3>
          <p className="text-gray-600 mb-4">
            We've sent a password reset link to <strong>{email}</strong>
          </p>
          <p className="text-sm text-gray-500">Redirecting to reset password page...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <p className="text-gray-600">
          Enter your email address and we'll send you a link to reset your password.
        </p>
      </div>

      <form className="space-y-5" onSubmit={handleSubmit}>
        {error && (
          <div className="rounded-xl bg-red-50 border border-red-200 p-4 shadow-sm">
            <p className="text-sm font-medium text-red-800">{error}</p>
          </div>
        )}

        <InputField
          type="email"
          label="Email address"
          name="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          placeholder="Enter your email"
          icon={<Mail className="text-gray-400" size={18} />}
        />

        <Button 
          type="submit" 
          className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200" 
          disabled={loading || !email} 
          loading={loading}
        >
          Send Reset Link
        </Button>
      </form>

      <div className="text-center">
        <Link
          to="/login"
          className="inline-flex items-center text-sm font-medium text-purple-600 hover:text-purple-500 transition-colors duration-200"
        >
          <ArrowLeft className="mr-1" size={16} />
          Back to login
        </Link>
      </div>
    </div>
  )
}

export default ForgotPasswordForm
